import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  authoirzeFormater,
  getLocaleDateString,
  getLocaleDateTimeString,
  history,
  STATE_OPTIONS,
  stateFormatter,
  toTitleCase,
} from '../utils';
import MovaticBtnsGroup from '../components/ButtonGroup';
import { Grid, IconButton, LinearProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EditIcon from '@mui/icons-material/Edit';
import React from 'react';
import Typography from '@mui/material/Typography';
import { SeverityPill } from '../components/Redesing/severity-pill';
import IconButtonMenu from '../components/Redesing/icon-button-menu';
import { Stack } from '@mui/system';
import ColumnAvatar from '../components/Redesing/column-avatar';
import { getHardwareIconByName } from '../util/utilsR';
import CustomAsyncToast from '../components/Redesing/custom-async-toast';
import { hardwareRemoteAction } from '../api/hardware';
import StatePills from '../components/Redesing/state-pills';
import OnlineOfflineIndicator from '../components/Redesing/online-offline-indicator';

const sendActions = (
  action,
  successMessage,
  errorMessage,
  loadingMessage,
  hardwareId
) => {
  return CustomAsyncToast({
    promise: () => hardwareRemoteAction(hardwareId, action),
    successMessage: () => successMessage,
    errorMessage: errorMessage,
    loadingMessage: loadingMessage,
  });
};

export function hardwareColumnsV2(
  type,
  systemType,
  remoteUnlockId,
  remoteLockId,
  remoteRingId,
  remoteActionLoaded,
  handleChangeState,
  isMobileScreen,
  isMediumScreen,
  hardwareAccess,
  showSystem = false
) {
  const identifierRowFormat = (row) => {
    const identifier =
      row.hardware_type_name === systemType
        ? row.number
        : row[row.primary_identifier]
        ? row[row.primary_identifier]
        : row?.label
        ? row.label
        : row.gid ?? row?.number;

    const identifierName =
      row.hardware_type_name === systemType
        ? 'Number'
        : row.primary_identifier && row[row.primary_identifier]
        ? toTitleCase(row.primary_identifier.replace(/_/g, ' '))
        : 'ID';

    const hardwareType = row?.hardware_type
      ? row.hardware_type.name
      : row.hardware_type_name;

    const { Icon, bgColor } = getHardwareIconByName(hardwareType);
    return (
      <ColumnAvatar
        avatar={<Icon />}
        avatarColor={bgColor}
        // The identifier is usually free text — a label like "7166 1234" — and
        // ColumnAvatar's default wrapper lets it wrap, then shows only the first
        // line. A space is a break opportunity, so the column's min-content shrinks
        // to the longest word and a narrow screen squeezes it that far: the label
        // wrapped and rendered as "7166…". Keep it on one line so the column has to
        // be wide enough for it, capped so one long label can't take over the row —
        // past the cap it ellipsizes instead of hiding a second line.
        noEllipsis
        firstRow={
          <Typography
            color="text.primary"
            variant="body1"
            noWrap
            sx={{ pl: 1, maxWidth: 160 }}
          >
            {identifier}
          </Typography>
        }
        secondRow={
          <Typography color="text.secondary" variant="body2">
            <SeverityPill color={'info'}>{identifierName}</SeverityPill>
          </Typography>
        }
      />
    );
  };
  const isSmallScreen = isMediumScreen || isMobileScreen;
  const stateColumn = {
    id: 'state',
    label: `State`,
    align: 'center',
    notSortable: true,
    exportFormat: (row) => stateFormatter(row.state).name,
    format: (row) => {
      return (
        <Stack justifyContent={'center'} alignItems={'center'}>
          <StatePills
            mapState={(state) => stateFormatter(state).name}
            stateOptions={STATE_OPTIONS.filter((item) => item.hardware)}
            row={row}
            handleChangeState={handleChangeState}
            dataId={`hardware-status`}
          />
        </Stack>
      );
    },
  };
  const batteryColumn = {
    id: 'battery_percent',
    label: 'Battery',
    align: 'center',
    // Sorted server-side by percentage then voltage (see buildHardwareSort).
    // Exports the same string the cell shows, like every other column here.
    // Without this the exporter falls back to row.battery_percent, so a unit that
    // reports only voltage exported an empty cell while the UI showed "4100 mV".
    exportFormat: (row) =>
      row?.battery_percent
        ? `${row.battery_percent}%`
        : row?.battery_voltage
        ? `${row.battery_voltage} mV`
        : 'Unknown',
    format: (row) => {
      const cant = row?.battery_percent || row?.battery_voltage;
      const show = !!row?.battery_percent;
      return (
        <Stack justifyContent={'center'} alignItems={'center'}>
          {show && (
            <LinearProgress
              value={cant || 0}
              variant="determinate"
              color={cant >= 20 ? (cant > 50 ? 'success' : 'warning') : 'error'}
              sx={{
                height: 8,
                width: 36,
              }}
            />
          )}

          <SeverityPill color="secondary">
            {row?.battery_percent
              ? `${row.battery_percent}%`
              : row.battery_voltage
              ? `${row.battery_voltage} mV`
              : 'Unknown'}
          </SeverityPill>
        </Stack>
      );
    },
  };
  // N/A wins when the hardware isn't rentable or availability is unknown (null),
  // even if is_available happens to be true. Otherwise map the boolean directly.
  const availabilityPill = (row) => {
    if (row.is_rentable === false || row.is_available == null) {
      return { label: 'N/A', color: 'info' };
    }
    return row.is_available
      ? { label: 'Yes', color: 'success' }
      : { label: 'No', color: 'error' };
  };
  const availableColumn = {
    id: 'available',
    label: 'Available',
    align: 'center',
    notSortable: true,
    exportFormat: (row) => availabilityPill(row).label,
    format: (row) => {
      const { label, color } = availabilityPill(row);
      return (
        <Stack justifyContent={'center'} alignItems={'center'}>
          <SeverityPill color={color}>{label}</SeverityPill>
        </Stack>
      );
    },
  };
  // Follows availabilityPill's N/A rule — N/A when the unit isn't rentable or the
  // rental state is unknown (null) — but the colors are inverted relative to
  // Available, because being in a rental is what makes a unit unavailable to the
  // next rider: Yes is `warning`, and No is `success` since an idle rentable unit
  // is the healthy state.
  const inRentalPill = (row) => {
    if (row.is_rentable === false || row.is_in_rental == null) {
      return { label: 'N/A', color: 'info' };
    }
    return row.is_in_rental
      ? { label: 'Yes', color: 'warning' }
      : { label: 'No', color: 'success' };
  };
  const inRentalColumn = {
    id: 'in_rental',
    label: 'In Rental',
    align: 'center',
    notSortable: true,
    exportFormat: (row) => inRentalPill(row).label,
    format: (row) => {
      const { label, color } = inRentalPill(row);
      return (
        <Stack justifyContent={'center'} alignItems={'center'}>
          <SeverityPill color={color}>{label}</SeverityPill>
        </Stack>
      );
    },
  };
  const columns = [
    {
      id: 'hardware_type_name',
      label: 'Type',
      notSortable: true,
      exportFormat: (row) => toTitleCase(row.hardware_type_name),
      format: identifierRowFormat,
    },
    {
      id: 'id',
      align: 'left',
      label: `Identifier`,
      hidden: true,
      searchable: true,
      notSortable: true,
    },
    {
      id: 'status',
      label: `Online`,
      align: 'center',
      notSortable: true,
      exportFormat: (row) =>
        row.remote_connection_async === null
          ? 'N/A'
          : row.remote_connection_async
          ? 'Online'
          : 'Offline',
      format: (row) => {
        return (
          <Stack justifyContent={'center'} alignItems={'center'}>
            {row.remote_connection_async === null ? (
              <SeverityPill color="secondary">N/A</SeverityPill>
            ) : (
              <OnlineOfflineIndicator isOnline={row.remote_connection_async} />
            )}{' '}
          </Stack>
        );
      },
    },
    // In Rental sits immediately right of Available at every breakpoint.
    ...(isSmallScreen
      ? [batteryColumn, stateColumn, availableColumn, inRentalColumn]
      : [stateColumn, availableColumn, inRentalColumn, batteryColumn]),
    {
      id: 'station_name',
      align: 'left',
      label: 'Location',
      searchable: true,
      width: '30%',
      notSortable: true,
    },
    {
      id: 'number',
      align: 'left',
      label: `Number`,
      hidden: true,
      searchable: true,
    },
    {
      id: 'imei',
      align: 'left',
      label: `IMEI`,
      hidden: true,
      searchable: true,
    },
    {
      id: 'mac',
      align: 'left',
      label: `Mac Address`,
      hidden: true,
      searchable: true,
    },
    {
      id: 'qr_id',
      align: 'left',
      label: `QR Code`,
      hidden: true,
      searchable: true,
    },

    {
      id: 'vendor_serial',
      align: 'left',
      label: `Vendor Serial`,
      hidden: true,
      searchable: true,
    },
    {
      id: 'vendor_qr_code',
      align: 'left',
      label: `Vendor QR Code`,
      hidden: true,
      searchable: true,
    },
    ...(showSystem
      ? [
          {
            id: 'system_name',
            label: 'System',
            searchable: true,
            notSortable: true,
            hidden: false,
            format: (row) => {
              return row?.system ? row.system.name : ' ';
            },
          },
        ]
      : []),
    {
      id: 'open_tickets',
      label: 'Open Tickets',
      align: 'center',
      exportFormat: (row) => row?.open_ticket_count ?? 0,
      format: (row) => {
        const openTickets = row?.open_ticket_count || 0;
        return (
          <Stack justifyContent={'center'} alignItems={'center'}>
            <SeverityPill
              color={openTickets > 0 || row?.has_safety_ticket ? 'error' : 'success'}
            >
              {row?.has_safety_ticket ? `Safety-Critical   ⚠️` : openTickets}
            </SeverityPill>
          </Stack>
        );
      },
    },
    {
      id: 'overdue_tickets',
      label: 'Overdue Tickets',
      align: 'center',
      exportFormat: (row) => row?.overdue_ticket_count ?? 0,
      format: (row) => {
        const openTickets = row?.overdue_ticket_count || 0;
        return (
          <Stack justifyContent={'center'} alignItems={'center'}>
            <SeverityPill color={openTickets > 0 ? 'error' : 'success'}>
              {openTickets}
            </SeverityPill>
          </Stack>
        );
      },
    },
  ];
  if (hardwareAccess) {
    columns.splice(isSmallScreen ? 3 : 1, 0, {
      id: 'actions',
      label: 'Actions',
      width: '25%',
      notSortable: true,
      shouldNotExport: true,
      disableCLick: true,
      align: 'center',
      format: (row) => {
        const buttons = [
          {
            label: '',
            startIcon: <LockOpenIcon />,
            onClick: (e) => {
              sendActions(
                'unlock',
                'Unlock request sent successfully',
                'There was an issue while sending the unlock request, please try again!',
                'Sending unlock request...',
                row.old_id
              );
            },
            disabled:
              remoteUnlockId === row.id ||
              !row.remote_connection_async ||
              !remoteActionLoaded,
            shouldRender: row.supports_remote_unlock,
          },
          {
            label: isMediumScreen || isMobileScreen ? 'Edit' : null,
            startIcon: <EditIcon />,
            onClick: (e) => {
              history.push(`/hardware/${row.id}/edit`);
            },
            disabled: false,
            shouldRender: true, // Set your condition here
          },

          {
            label: 'Remote Lock',
            startIcon: <LockIcon />,
            onClick: (e) => {
              sendActions(
                'lock',
                'Lock request sent successfully',
                'There was an issue while sending the lock request, please try again!',
                'Sending lock request...',
                row.old_id
              );
            },
            disabled:
              remoteLockId === row.id ||
              !row.remote_connection_async ||
              !remoteActionLoaded,
            shouldRender: row.supports_remote_lock,
          },
          {
            label: 'Remote Ring',
            startIcon: <NotificationsActiveIcon />,
            onClick: (e) => {
              sendActions(
                'ring',
                'Ring request sent successfully',
                'There was an issue while sending the ring request, please try again!',
                'Sending ring request...',
                row.old_id
              );
            },
            disabled:
              remoteRingId === row.id ||
              !row.remote_connection_async ||
              !remoteActionLoaded,
            shouldRender: row.supports_remote_ring,
          },
        ];
        return (
          <Stack justifyContent={'center'} alignItems={'center'}>
            <IconButtonMenu
              showEllipsis={isMediumScreen || isMobileScreen}
              slice={isMobileScreen ? 0 : isMediumScreen ? 1 : 2}
              buttons={buttons.filter((item) => item.shouldRender)}
            />
          </Stack>
        );
      },
    });
  }
  return columns;
}

export const ticketColumns = [
  {
    id: 'actions',
    align: 'left',
    title: 'Actions',
    width: '25%',
    shouldNotExport: true,
    noSort: true,
    activeFormatter: (cell, row) => {
      const arrayBtns = [
        {
          text: (
            <div>
              <i className="fa fa-eye" style={{ color: 'white' }} />
            </div>
          ),
          onClick: (e) => {
            e.stopPropagation();
            history.push(`/maintenance/${row.id}`);
          },
          shouldRender: true,
          shouldDisable: false,
        },
      ];

      return (
        <MovaticBtnsGroup arrayBtns={arrayBtns} btnGroupClass="btn-group-sm alignLeft" />
      );
    },
  },
  {
    id: 'due_on',
    title: 'Due',
    width: '20%',
    activeFormatter: (due_on) => {
      let due = new Date(due_on);
      if (due === 'Invalid Date') {
        // probably safari
        due = new Date(due_on.replace(/ /g, 'T'));
      }
      return due.toLocaleDateString();
    },
    className: (due_on, row) => {
      const due = new Date(due_on);
      if (
        due.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) &&
        !row.completed_on &&
        !row.canceled_on
      ) {
        return { className: 'bad' };
      }
    },
  },
  {
    id: 'status',
    title: 'Status',
    width: '20%',
    activeFormatter: (cell) => {
      return cell ? cell : ' ';
    },
  },
  {
    id: 'description',
    title: 'Description',
    width: '60%',
    activeFormatter: (cell) => {
      return cell ? cell : ' ';
    },
  },
];

export const ticketColumnsV2 = [
  {
    id: 'due_on',
    label: 'Due',
    format: (row) => {
      let due = new Date(row.due_on);
      if (due === 'Invalid Date') {
        // probably safari
        due = new Date(row.due_on.replace(/ /g, 'T'));
      }
      return (
        <SeverityPill
          color={
            due.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) &&
            !row.completed_on &&
            !row.canceled_on
              ? 'error'
              : 'success'
          }
        >
          {getLocaleDateString(row.due_on)}
        </SeverityPill>
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    format: (row) => {
      if (!row?.status) return ' ';
      const lower = row.status.toLowerCase();
      const color =
        lower === 'closed' ? 'success' : lower === 'canceled' ? 'error' : 'warning';
      return <SeverityPill color={color}>{row.status}</SeverityPill>;
    },
  },
  {
    id: 'description',
    label: 'Description',
    activeFormatter: (row) => {
      return row?.description ? row.description : ' ';
    },
  },
];

export const taskColumns = [
  {
    id: 'actions',
    align: 'left',
    title: 'Actions',
    width: '25%',
    shouldExport: false,
    shouldNotExport: true,
    noSort: true,
    activeFormatter: (cell, row) => {
      const arrayBtns = [
        {
          text: (
            <div>
              <i className="fa fa-eye" style={{ color: 'white' }} />
            </div>
          ),
          onClick: (e) => {
            e.stopPropagation();
            history.push(`/maintenance/${row.id}`);
          },
          shouldRender: true,
          shouldDisable: false,
        },
      ];

      return (
        <MovaticBtnsGroup arrayBtns={arrayBtns} btnGroupClass="btn-group-sm alignLeft" />
      );
    },
  },
  {
    id: 'created_on',
    title: 'Closed',
    width: '20%',
    activeFormatter: (date) => getLocaleDateString(date),
  },
  {
    id: 'admin_name',
    title: 'Completed By',
    width: '20%',
    activeFormatter: (assignee_name) => {
      if (!assignee_name) {
        return 'Unassigned';
      }
      return assignee_name;
    },
  },
  {
    id: 'task_name',
    title: 'Maintenance Type',
    width: '60%',
    activeFormatter: (cell) => {
      return cell ? cell : ' ';
    },
  },
];

export const taskColumnsV2 = [
  {
    id: 'created_on',
    label: 'Closed',
    format: (row) => getLocaleDateString(row.created_on),
  },
  {
    id: 'admin_name',
    label: 'Completed By',
    format: (row) => {
      if (!row.admin_name) {
        return 'Unassigned';
      }
      return row.admin_name;
    },
  },
  {
    id: 'task_name',
    label: 'Maintenance Type',
    format: (row) => {
      return row?.task_name ? row.task_name : ' ';
    },
  },
];

export const eventLogsColumns = [
  {
    id: 'created_on',
    label: 'Created',
    notSortable: true,
    format: (row) => getLocaleDateTimeString(row.created_on),
  },
  {
    id: 'recorded_at',
    label: 'Recorded',
    format: (row) => getLocaleDateTimeString(row.recorded_at),
  },
  {
    id: 'type',
    label: 'Type',
    notSortable: true,
    format: (row) => {
      if (!row.type) {
        return 'N/A';
      }
      if (row.color_type) {
        return <SeverityPill color={row.color_type}>{row.type}</SeverityPill>;
      }
      return row.type;
    },
  },
];

export const hardwareRentalColumnsV2 = [
  {
    id: 'start_station',
    label: 'Start Location',
    format: (row) => {
      return row?.start_station ? row.start_station : ' ';
    },
    searchable: true,
  },
  {
    id: 'end_station',
    label: 'End Location',
    format: (row) => {
      return row?.end_station ? row.end_station : ' ';
    },
    searchable: true,
  },
  {
    id: 'start_time',
    label: 'Start Time',
    format: (row) => (row?.start_time ? getLocaleDateTimeString(row.start_time) : ' '),
  },
  {
    id: 'end_time',
    label: 'End Time',
    format: (row) => (row?.end_time ? getLocaleDateTimeString(row.end_time) : ' '),
  },
];

export function assignmentColumnsV2(hardware, removeAssigment, goToHardware) {
  return [
    {
      id: 'actions',
      label: 'Actions',
      width: '25%',
      shouldNotExport: true,
      notSortable: true,
      format: (row) => {
        const btns = [
          {
            button: (
              <IconButton
                sx={{
                  borderRadius: '10px',
                  '&:hover': {
                    borderRadius: '10px',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeAssigment(row);
                }}
                disabled={!!row?.removed_on}
              >
                <DeleteIcon />
              </IconButton>
            ),
            shouldRender: true,
          },
        ];
        return (
          <Grid container spacing={1}>
            {btns
              .filter((item) => item.shouldRender)
              .map((btn) => (
                <Grid item>{btn.button}</Grid>
              ))}
          </Grid>
        );
      },
    },
    {
      id: 'hardware_type_name',
      align: 'left',
      label: 'Type',
      width: '20%',
      searchable: true,
      format: (row) => {
        return toTitleCase(
          row?.child_hardware_type_name || row?.parent_hardware_type_name
        );
      },
    },
    {
      id: 'child_id',
      label: 'Assignee',
      searchable: true,
      format: (row) => row?.child && row[row.primary_identifier],
    },
    {
      id: 'created_on',
      label: 'Assigned On',
      searchable: true,
      format: (row) => {
        return getLocaleDateTimeString(row?.created_on) || 'N/A';
      },
    },
    {
      id: 'removed_on',
      label: 'Unassigned On',
      searchable: true,
      format: (row) => {
        return getLocaleDateTimeString(row?.removed_on) || 'N/A';
      },
    },
  ];
}

// OLD HARDWARE
// TODO: remove this
export function columnsForLocks(title, vendor) {
  const columns = [
    {
      id: 'mac',
      align: 'left',
      title: 'Lock #',
      width: '20%',
      activeFormatter: (cell) => {
        return cell ? cell : ' ';
      },
    },
    title === 'Room'
      ? {
          id: 'stationName',
          align: 'left',
          title: `${title} Name`,
          width: '20%',
          activeFormatter: (cell) => {
            return cell ? cell : ' ';
          },
        }
      : {
          id: 'hardwareNumber',
          align: 'left',
          title: `${title} #`,
          width: '20%',
          activeFormatter: (hardwareNumber) => {
            if (hardwareNumber) return hardwareNumber;
            return `No ${title}`;
          },
        },
    {
      id: 'state',
      align: 'left',
      title: 'Current Status',
      width: '20%',
      activeFormatter: authoirzeFormater,
    },
    {
      id: 'battery_status',
      align: 'left',
      title: 'Power Level',
      width: '20%',
      activeFormatter: (battery_status) => {
        if (battery_status) return 'Good';
        if (!battery_status) return `Low`;
        return 'Unknown';
      },
    },
  ];

  if (vendor === 'noke') {
    columns.splice(1, 0, {
      id: 'nokeSerialNumber',
      align: 'left',
      title: 'Serial Number',
      activeFormatter: (cell) => {
        return cell ? cell : ' ';
      },
    });
  }

  return columns;
}

// OLD HARDWARE
// TODO: remove this
export function makeColumnsForUnits(title, path) {
  const array = [
    {
      id: 'actions',
      align: 'left',
      title: 'Actions',
      width: '25%',
      shouldNotExport: true,
      noSort: true,
      activeFormatter: (cell, row) => {
        const arrayBtns = [
          {
            text: (
              <div>
                <i className="fa fa-eye" style={{ color: 'white' }} />
              </div>
            ),
            onClick: (e) => {
              e.stopPropagation();
              history.push(`${path}/${row.id}`);
            },
            shouldRender: true,
            shouldDisable: false,
          },
        ];

        return (
          <MovaticBtnsGroup
            arrayBtns={arrayBtns}
            btnGroupClass="btn-group-sm alignCenter"
          />
        );
      },
    },
    {
      id: 'id',
      hidden: true,
      title: 'ID',
      shouldExport: true,
    },
    {
      id: 'gid',
      hidden: true,
      title: 'GID',
      shouldExport: true,
    },
    {
      id: 'number',
      title: `${title} #`,
      width: '20%',
    },
    {
      id: 'state',
      align: 'left',
      title: 'Status',
      width: '20%',
      activeFormatter: (state) => stateFormatter(state).name,
    },
    {
      id: 'tableString',
      title: 'Location',
    },
    {
      hidden: true,
      id: 'iccid',
      shouldExport: true,
      title: 'ICCID',
    },
    {
      hidden: true,
      id: 'imei',
      shouldExport: true,
      title: 'IMEI',
    },
    {
      hidden: true,
      id: 'label',
      shouldExport: true,
      title: 'Label',
    },
    {
      hidden: true,
      id: 'firmware_version',
      shouldExport: true,
      title: 'Firmware Version',
    },
    {
      hidden: true,
      id: 'vendor_qr_code',
      shouldExport: true,
      title: 'Vendor QR Code',
    },
    {
      hidden: true,
      id: 'qr_id',
      shouldExport: true,
      title: 'Assigned QR Code',
    },
    {
      hidden: true,
      id: 'vendor_id',
      shouldExport: true,
      title: 'Vendor ID',
    },
    {
      hidden: true,
      id: 'last_seen',
      shouldExport: true,
      title: 'Last Seen',
      activeFormatter: (date) => getLocaleDateString(date),
    },
  ];

  if (title === 'Locker') {
    array.push({
      id: 'battery_status_string',
      title: 'Power Level',
      activeFormatter: (cell) => {
        return cell ? cell : ' ';
      },
    });
  }

  if (title === 'Keybox') {
    array.push(
      {
        id: 'nusetSerialNumber',
        title: 'Serial Number',
        activeFormatter: (cell) => {
          return cell ? cell : ' ';
        },
      },
      {
        id: 'pincodes',
        title: 'Pincodes Left',
        activeFormatter: (cell) => {
          return cell ? cell : ' ';
        },
      }
    );
  }

  return array;
}

// TODO: remove this
export function makeHardwareRentalColumns(unitTitle, stationTitle) {
  unitTitle = `${unitTitle} #`;
  const startStation = `${stationTitle}`;
  const endStation = `End ${stationTitle}`;
  if (unitTitle === 'Locker #' || unitTitle === 'Rack #') {
    return [
      {
        id: 'actions',
        align: 'left',
        title: 'Actions',
        width: '25%',
        shouldExport: false,
        noSort: true,
        activeFormatter: (cell, row) => {
          const arrayBtns = [
            {
              text: (
                <div>
                  <i className="fa fa-eye" style={{ color: 'white' }} />
                </div>
              ),
              onClick: (e) => {
                e.stopPropagation();
                history.push(`/rentals/${row.id}`);
              },
              shouldRender: true,
              shouldDisable: false,
            },
          ];

          return (
            <MovaticBtnsGroup
              arrayBtns={arrayBtns}
              btnGroupClass="btn-group-sm alignCenter"
            />
          );
        },
      },
      {
        id: 'startStationName',
        align: 'left',
        title: startStation,
        width: '20%',
        noSort: true,
        activeFormatter: (cell, row) => row.start?.station?.name || ' ',
      },
      {
        id: 'startTime',
        alignment: 'left',
        title: 'Start Time',
        width: '20%',
        activeFormatter: (cell, row) =>
          row.start && row.start.time ? getLocaleDateTimeString(row.start.time) : ' ',
      },
      {
        id: 'endTime',
        alignment: 'left',
        title: 'End Time',
        width: '20%',
        noSort: true,
        activeFormatter: (cell, row) =>
          row.end && row.end.time ? getLocaleDateTimeString(row.end.time) : ' ',
      },
    ];
  }
  return [
    {
      id: 'actions',
      alignment: 'left',
      title: 'Actions',
      width: '25%',
      shouldExport: false,
      noSort: true,
      activeFormatter: (cell, row) => {
        const arrayBtns = [
          {
            text: (
              <div>
                <i className="fa fa-eye" style={{ color: 'white' }} />
              </div>
            ),
            onClick: (e) => {
              e.stopPropagation();
              history.push(`/rentals/${row.id}`);
            },
            shouldRender: true,
            shouldDisable: false,
          },
        ];

        return (
          <MovaticBtnsGroup
            arrayBtns={arrayBtns}
            btnGroupClass="btn-group-sm alignCenter"
          />
        );
      },
    },
    {
      id: 'startStationName',
      alignment: 'left',
      title: startStation,
      width: '20%',
      noSort: true,
      activeFormatter: (cell, row) => row.start?.station?.name || ' ',
    },
    {
      id: 'endStationName',
      alignment: 'left',
      title: endStation,
      width: '20%',
      noSort: true,
      activeFormatter: (cell, row) => row.end?.station?.name || ' ',
    },
    {
      id: 'startTime',
      alignment: 'left',
      title: 'Start Time',
      width: '20%',
      activeFormatter: (cell, row) =>
        row.start && row.start.time ? getLocaleDateTimeString(row.start.time) : ' ',
    },
    {
      id: 'endTime',
      alignment: 'left',
      title: 'End Time',
      width: '20%',
      noSort: true,
      activeFormatter: (cell, row) =>
        row.end && row.end.time ? getLocaleDateTimeString(row.end.time) : ' ',
    },
  ];
}
